import React, { useState } from "react";
import { Loader, Check, House, Building2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
export default function FindYourFit() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [isOpen, setIsOpen] = useState(false);
  return <section id="find-your-fit" className="bg-muted py-24 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div ref={ref} className={`mx-auto max-w-2xl text-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl font-serif font-semibold text-foreground sm:text-4xl mb-4">
            Not sure where to start?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-lg mx-auto">Tell me a bit about your lifestyle and budget, and I will curate a list of properties that match your unique needs.</p>

          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button size="lg" className="text-base px-8 h-12 shadow-lg hover:shadow-xl transition-all">
                Personalized Recommendation
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif">Find Your Fit</DialogTitle>
                <DialogDescription>
                  Complete this short quiz to help us find your perfect home in Orange County.
                </DialogDescription>
              </DialogHeader>
              
              <div className="mt-4">
                <FormProvider formId="find_fit_form_communities" sectionName="find-your-fit" defaultValues={{
                name: "",
                email: "",
                phone: "",
                budget: "",
                property_type: "Buy a House",
                community_features: []
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                budget: {
                  required: true,
                  min: 50000,
                  max: 5000000
                },
                property_type: {
                  required: true
                }
              }} submitText="Get Recommendations" successMessage="Thanks! We've received your preferences and will be in touch with matches soon.">
                  <QuizForm />
                </FormProvider>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>;
}
function QuizForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
          <Check className="h-8 w-8" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">All Set!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Close
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        {}
        <div className="sm:col-span-2 space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider border-b pb-2">
            Contact Information
          </h4>
        </div>

        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="John Doe" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="john@example.com" className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="sm:col-span-2">
              <FormLabel>Phone Number <span className="text-muted-foreground font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="(555) 123-4567" className={inputClasses} />
              </FormControl>
              <FormDescription>For a quick follow-up call.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <div className="sm:col-span-2 space-y-4 mt-2">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider border-b pb-2">
            Your Preferences
          </h4>
        </div>

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel>Budget (USD)</FormLabel>
              <FormControl>
                <input {...field} type="number" placeholder="500000" className={inputClasses} />
              </FormControl>
              <FormDescription>Max price or monthly rent.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="property_type" render={({
        field
      }) => <FormItem>
              <FormLabel>Looking To</FormLabel>
              <FormControl>
                <div className="flex gap-4">
                  {["Buy a House", "Rent a House"].map(option => <label key={option} className={`flex-1 flex flex-col items-center justify-center p-3 rounded-lg border cursor-pointer transition-all hover:bg-accent ${field.value === option ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-input bg-background"}`}>
                      <input type="radio" {...field} value={option} checked={field.value === option} className="sr-only" onChange={() => field.onChange(option)} />
                      {option === "Buy a House" ? <House className="mb-2 h-5 w-5 text-primary" /> : <Building2 className="mb-2 h-5 w-5 text-primary" />}
                      <span className="text-sm font-medium">{option}</span>
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="community_features" render={({
        field
      }) => <FormItem className="sm:col-span-2">
              <FormLabel>Preferred Community Features</FormLabel>
              <FormControl>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  {["Good Schools", "Walkable Neighborhood", "Family-Friendly", "Pet-Friendly", "Close to Transit"].map(option => {
              const isChecked = (field.value || []).includes(option);
              return <label key={option} className="flex items-center space-x-3 space-y-0 rounded-md border p-3 hover:bg-accent cursor-pointer transition-colors">
                        <input type="checkbox" checked={isChecked} onChange={e => {
                  const checked = e.target.checked;
                  const current = field.value || [];
                  if (checked) {
                    field.onChange([...current, option]);
                  } else {
                    field.onChange(current.filter((val: string) => val !== option));
                  }
                }} className="h-4 w-4 rounded border-primary text-primary focus:ring-primary" />
                        <span className="text-sm font-normal leading-none">
                          {option}
                        </span>
                      </label>;
            })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
          {submitError}
        </div>}

      <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </> : "Get Recommendations"}
      </Button>
    </Form>;
}